import { AlertTriangle, Pencil } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { formatBRL } from '@/lib/utils';
import type { FiscalReport } from '@/features/fiscal/schemas';

interface Props {
  reports: FiscalReport[];
  reportedValue: number | null;
  onAdjust: (value: number) => Promise<unknown> | void;
  isAdjusting?: boolean;
  disabled?: boolean;
}

function ocrStatusLabel(status: FiscalReport['ocrStatus']): string {
  return {
    PENDING: 'Aguardando OCR',
    PROCESSING: 'Processando…',
    DONE: 'Concluído',
    FAILED: 'Falhou',
  }[status];
}

export function OcrPreview({
  reports,
  reportedValue,
  onAdjust,
  isAdjusting,
  disabled,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<string>(
    reportedValue != null ? reportedValue.toFixed(2) : '',
  );

  const latest = reports[0] ?? null;
  const confidence = latest?.ocrConfidence ?? null;
  const lowConfidence = confidence != null && confidence < 0.8;

  const submit = async (): Promise<void> => {
    const num = Number(value.replace(',', '.'));
    if (!Number.isFinite(num) || num < 0) return;
    await onAdjust(num);
    setEditing(false);
  };

  if (!latest) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>OCR do relatório</CardTitle>
        </CardHeader>
        <p className="text-sm text-(--color-fg-muted)">
          Nenhum relatório anexado ainda.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>OCR do relatório</CardTitle>
      </CardHeader>

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-(--color-fg-muted)">Status:</span>
          <span className="font-medium">{ocrStatusLabel(latest.ocrStatus)}</span>
          {confidence != null ? (
            <span className="text-(--color-fg-muted)">
              · Confiança {Math.round(confidence * 100)}%
            </span>
          ) : null}
        </div>

        {lowConfidence ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>Reveja — confiança baixa. Confirme o valor antes de liberar.</span>
          </div>
        ) : null}

        {latest.ocrError ? (
          <p role="alert" className="text-sm text-(--color-error)">
            Erro no OCR: {latest.ocrError}
          </p>
        ) : null}

        <div className="flex items-center justify-between rounded-md border border-(--color-border) p-3">
          <div>
            <p className="text-xs text-(--color-fg-muted)">Valor informado</p>
            {editing ? (
              <div className="mt-1 flex items-center gap-2">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={value}
                  aria-label="Valor informado pelo hospital"
                  onChange={(e) => setValue(e.target.value)}
                  className="w-32"
                />
                <Button size="sm" onClick={submit} disabled={isAdjusting}>
                  {isAdjusting ? 'Salvando…' : 'Salvar'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditing(false);
                    setValue(reportedValue != null ? reportedValue.toFixed(2) : '');
                  }}
                  disabled={isAdjusting}
                >
                  Cancelar
                </Button>
              </div>
            ) : (
              <p className="text-xl font-semibold">
                {reportedValue != null ? formatBRL(reportedValue) : '—'}
              </p>
            )}
          </div>
          {!editing ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(true)}
              disabled={disabled}
            >
              <Pencil className="mr-1 h-4 w-4" aria-hidden />
              Ajustar
            </Button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
